import React, { useState } from 'react';
import { Loader2, PlusCircle, Trash2, ArrowLeft } from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

export interface LineItem {
  description: string;
  amount: number;
  quantity: number;
}

interface CreatePaymentLinkPaneProps {
  businessId: string;
  orderId?: string;
  onSuccess: () => void;
  onCancel: () => void;
  initialTitle?: string;
  initialNotes?: string;
  initialLineItems?: LineItem[];
}

export const CreatePaymentLinkPane: React.FC<CreatePaymentLinkPaneProps> = ({
  businessId,
  orderId,
  onSuccess,
  onCancel,
  initialTitle = '',
  initialNotes = '',
  initialLineItems = [{ description: '', amount: 0, quantity: 1 }]
}) => {
  const toast = useToast();
  const [creating, setCreating] = useState(false);

  const [title, setTitle] = useState(initialTitle);
  const [notes, setNotes] = useState(initialNotes);
  const [lineItems, setLineItems] = useState<LineItem[]>(initialLineItems);
  const [expiresAt, setExpiresAt] = useState('');

  const handleAddLineItem = () => {
    if (lineItems.length >= 20) {
      toast.warning('Limit Reached', 'Maximum 20 line items allowed');
      return;
    }
    setLineItems([...lineItems, { description: '', amount: 0, quantity: 1 }]);
  };

  const handleUpdateLineItem = (index: number, field: keyof LineItem, value: any) => {
    const newItems = [...lineItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setLineItems(newItems);
  };

  const handleRemoveLineItem = (index: number) => {
    if (lineItems.length === 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || lineItems.some(item => !item.description.trim() || item.amount <= 0)) {
      toast.warning('Invalid Form', 'Please ensure all fields are filled correctly.');
      return;
    }
    setCreating(true);
    try {
      await apiClient.post('/payment-links', {
        businessId,
        title,
        notes,
        lineItems,
        expiresAt: expiresAt || undefined,
        orderId
      });
      toast.success('Success', 'Payment link created successfully');
      onSuccess();
    } catch (e: any) {
      toast.error('Creation Failed', e.response?.data?.error || 'Failed to create payment link');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="w-full h-full bg-light-card dark:bg-dark-card border-l border-light-border dark:border-dark-border flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-6 py-4 flex items-center gap-3">
        <button onClick={onCancel} className="p-2 -ml-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-full transition-colors text-light-subtle dark:text-dark-subtle">
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-xl font-bold text-light-text dark:text-dark-text">Create Payment Link</h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
        <form onSubmit={handleCreate} className="space-y-4 pb-12">
          <div>
            <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase mb-1.5">Title / Reference *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Website Development Invoice"
              className="w-full bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-xl px-4 py-2.5 text-light-text dark:text-dark-text focus:outline-none focus:border-primary"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase mb-1.5">Description / Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Additional context for the customer..."
              className="w-full bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-xl px-4 py-2.5 text-light-text dark:text-dark-text focus:outline-none focus:border-primary resize-none h-20"
            />
          </div>

          <div className="border border-light-border dark:border-dark-border rounded-xl p-4 bg-light-bg/50 dark:bg-dark-bg/50">
            <div className="flex justify-between items-center mb-4">
              <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase">Line Items *</label>
              <button type="button" onClick={handleAddLineItem} className="text-primary text-sm font-bold flex items-center gap-1 hover:underline">
                <PlusCircle size={14} /> Add Item
              </button>
            </div>
            <div className="space-y-3">
              {lineItems.map((item, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={item.description}
                      onChange={e => handleUpdateLineItem(index, 'description', e.target.value)}
                      placeholder="Item Description"
                      className="w-full bg-white dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg px-3 py-2 text-sm text-light-text dark:text-dark-text focus:outline-none focus:border-primary"
                      required
                    />
                  </div>
                  <div className="w-20">
                    <input
                      type="number"
                      value={item.quantity || ''}
                      onChange={e => handleUpdateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                      placeholder="Qty"
                      min="1"
                      className="w-full bg-white dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg px-3 py-2 text-sm text-light-text dark:text-dark-text focus:outline-none focus:border-primary"
                      required
                    />
                  </div>
                  <div className="w-24">
                    <input
                      type="number"
                      value={item.amount || ''}
                      onChange={e => handleUpdateLineItem(index, 'amount', parseInt(e.target.value, 10) || 0)}
                      placeholder="Amount"
                      min="0"
                      step="1"
                      className="w-full bg-white dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg px-3 py-2 text-sm text-light-text dark:text-dark-text focus:outline-none focus:border-primary"
                      required
                    />
                  </div>
                  {lineItems.length > 1 && (
                    <button type="button" onClick={() => handleRemoveLineItem(index)} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition mt-0.5">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 text-right">
              <span className="text-sm text-light-subtle dark:text-dark-subtle">Total: </span>
              <span className="text-lg font-bold text-light-text dark:text-dark-text">
                KSH {lineItems.reduce((acc, curr) => acc + (curr.amount * curr.quantity), 0).toLocaleString()}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-light-subtle dark:text-dark-subtle uppercase mb-1.5">Expires At (Optional)</label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
              className="w-full bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-xl px-4 py-2.5 text-light-text dark:text-dark-text focus:outline-none focus:border-primary text-sm"
            />
            <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">If left blank, defaults to 24 hours.</p>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={creating}
              className="w-full bg-primary text-white py-3 rounded-xl font-bold flex items-center justify-center hover:bg-primary/90 transition disabled:opacity-50"
            >
              {creating ? <Loader2 size={18} className="animate-spin" /> : 'Generate Payment Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
